import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trophy } from "lucide-react";
import { toast } from "sonner";

const levels = ["Bronce", "Plata", "Oro", "Platino", "Diamante", "Histórico"];

export default function AdminRaffles() {
  const [raffles, setRaffles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clientTags, setClientTags] = useState([]);
  const [entriesByRaffle, setEntriesByRaffle] = useState({});

  const load = async () => {
    const [rafflesResp, tagsResp] = await Promise.all([
      api.get("/admin/raffles"),
      api.get("/admin/client-tags"),
    ]);
    const rafflesData = rafflesResp.data || [];
    setRaffles(rafflesData);
    setClientTags(tagsResp.data?.tags || []);

    const entriesPairs = await Promise.all(
      rafflesData.map(async (raffle) => {
        try {
          const { data } = await api.get(`/admin/raffles/${raffle.id}/entries`);
          return [raffle.id, data || []];
        } catch {
          return [raffle.id, []];
        }
      })
    );
    setEntriesByRaffle(Object.fromEntries(entriesPairs));
  };

  useEffect(() => { load(); }, []);

  const updateRaffle = (id, patch) => {
    setRaffles((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
  };

  const createRaffle = async () => {
    setSaving(true);
    try {
      await api.post("/admin/raffles", {
        title: "Nuevo sorteo KAPEI",
        description: "Describe aquí el sorteo.",
        prize: "Indica el premio",
        active: false,
        min_level: "Bronce",
        required_tag: "",
        min_pages: 1,
        max_entries_per_user: 5,
        max_winners: 1,
        status: "draft",
      });
      toast.success("Sorteo creado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const notifyRaffle = async (raffle) => {
    if (!window.confirm(`¿Enviar notificación de este sorteo a los clientes afectados?\n\n${raffle.title || "Sorteo KAPEI"}`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/raffles/${raffle.id}/notify`);
      toast.success(`Notificados: ${data.notified}. Push OK: ${data.push_ok}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const importFinishedOrders = async (raffle) => {
    if (!window.confirm(`¿Importar pedidos ya terminados para este sorteo?\n\n${raffle.title || "Sorteo KAPEI"}\n\nSe revisarán pedidos terminados dentro del rango de fechas y se crearán participaciones sin duplicar.`)) return;

    setSaving(true);
    try {
      const { data } = await api.post(`/admin/raffles/${raffle.id}/import-finished-orders`);
      toast.success(`Importados: ${data.created}. Revisados: ${data.checked}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const drawRaffle = async (raffle, force = false) => {
    const entries = entriesByRaffle[raffle.id] || [];
    if (!entries.length) {
      toast.error("No hay participaciones para este sorteo");
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const isStillRunning = raffle.ends_at && today <= raffle.ends_at;

    if (isStillRunning && !force) {
      toast.error(`El sorteo todavía está en curso. Finaliza el ${raffle.ends_at}.`);
      return;
    }

    const msg = force
      ? `⚠️ FORZAR SORTEO ANTES DE TIEMPO\n\n${raffle.title || "Sorteo KAPEI"}\n\nFinaliza el: ${raffle.ends_at || "sin fecha"}\nParticipaciones: ${entries.length}\n\n¿Seguro que quieres cerrarlo y elegir ganador ahora?`
      : `¿Realizar el sorteo ahora?\n\n${raffle.title || "Sorteo KAPEI"}\n\nParticipaciones: ${entries.length}`;

    if (!window.confirm(msg)) return;

    setSaving(true);
    try {
      const { data } = await api.post(`/admin/raffles/${raffle.id}/draw${force ? "?force=true" : ""}`);
      const codes = (data.winner_order_codes || []).join(", ");
      toast.success(`Ganador: ${codes}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveRaffle = async (raffle) => {
    setSaving(true);
    try {
      await api.put(`/admin/raffles/${raffle.id}`, raffle);
      toast.success("Sorteo guardado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#4d7c0f] text-white">
          <Trophy className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-black text-slate-900">Sorteos</h1>
          <p className="text-sm text-slate-600">
            Crea y modifica sorteos del Club KAPEI. Los clientes solo verán los sorteos activos que les correspondan.
          </p>
        </div>
        <Button onClick={createRaffle} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
          Nuevo sorteo
        </Button>
      </div>

      <div className="flex flex-col gap-5">
        {raffles.map((raffle) => (
          <section key={raffle.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                  {raffle.status || "draft"}
                </div>
                <h2 className="text-xl font-black text-slate-900">{raffle.title || "Sorteo sin título"}</h2>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <Label className="font-bold">Activo</Label>
                <Switch
                  checked={!!raffle.active}
                  onCheckedChange={(v) => updateRaffle(raffle.id, { active: v })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Título visible</Label>
                <Input value={raffle.title || ""} onChange={(e) => updateRaffle(raffle.id, { title: e.target.value })} className="mt-1" />
              </div>

              <div className="md:col-span-2">
                <Label>Descripción visible</Label>
                <Textarea value={raffle.description || ""} onChange={(e) => updateRaffle(raffle.id, { description: e.target.value })} className="mt-1 min-h-[90px]" />
              </div>

              <div className="md:col-span-2">
                <Label>Premio</Label>
                <Input value={raffle.prize || ""} onChange={(e) => updateRaffle(raffle.id, { prize: e.target.value })} className="mt-1" />
              </div>

              <div>
                <Label>Inicio del sorteo</Label>
                <Input type="date" value={raffle.starts_at || ""} onChange={(e) => updateRaffle(raffle.id, { starts_at: e.target.value })} className="mt-1" />
              </div>

              <div>
                <Label>Fin del sorteo</Label>
                <Input type="date" value={raffle.ends_at || ""} onChange={(e) => updateRaffle(raffle.id, { ends_at: e.target.value })} className="mt-1" />
              </div>

              <div>
                <Label>Premio reclamable hasta</Label>
                <Input type="date" value={raffle.claim_until || ""} onChange={(e) => updateRaffle(raffle.id, { claim_until: e.target.value })} className="mt-1" />
              </div>

              <div>
                <Label>Nivel mínimo</Label>
                <Select value={raffle.min_level || "Bronce"} onValueChange={(v) => updateRaffle(raffle.id, { min_level: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {levels.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Etiqueta requerida</Label>
                <Select value={raffle.required_tag || "none"} onValueChange={(v) => updateRaffle(raffle.id, { required_tag: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin etiqueta requerida</SelectItem>
                    {clientTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Páginas mínimas por pedido participante</Label>
                <Input
                  type="number"
                  min={1}
                  value={raffle.min_pages || 1}
                  onChange={(e) => updateRaffle(raffle.id, { min_pages: parseInt(e.target.value || "1", 10) })}
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-slate-500">Usa 1 para “sin mínimo real”.</p>
              </div>

              <div>
                <Label>Máximo participaciones por cliente</Label>
                <Input
                  type="number"
                  min={1}
                  value={raffle.max_entries_per_user || 5}
                  onChange={(e) => updateRaffle(raffle.id, { max_entries_per_user: parseInt(e.target.value || "5", 10) })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Número de ganadores</Label>
                <Input
                  type="number"
                  min={1}
                  value={raffle.max_winners || 1}
                  onChange={(e) => updateRaffle(raffle.id, { max_winners: parseInt(e.target.value || "1", 10) })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Estado interno</Label>
                <Select value={raffle.status || "draft"} onValueChange={(v) => updateRaffle(raffle.id, { status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="closed">Cerrado</SelectItem>
                    <SelectItem value="awarded">Premiado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Notas internas</Label>
                <Textarea
                  value={raffle.internal_notes || ""}
                  onChange={(e) => updateRaffle(raffle.id, { internal_notes: e.target.value })}
                  className="mt-1 min-h-[80px]"
                  placeholder="Notas solo visibles para KAPEI"
                />
              </div>
            </div>

            {raffle.winner_order_codes?.length > 0 && (
              <div className="mt-5 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-black text-amber-900">
                  🏆 {raffle.winner_order_codes.length > 1 ? "Ganadores" : "Ganador"}
                </div>

                <div className="mt-3 space-y-2">
                  {(raffle.winner_entries || []).length > 0 ? (
                    raffle.winner_entries.map((winner) => (
                      <div key={winner.entry_id || winner.order_code} className="rounded-xl bg-white/80 p-3">
                        <div className="text-xl font-black text-slate-900">
                          {winner.order_code}
                        </div>
                        <div className="text-sm font-semibold text-amber-900">
                          {winner.client_name || "Cliente"}{winner.client_email ? ` · ${winner.client_email}` : ""}
                        </div>
                        <div className="text-xs font-bold text-slate-500">
                          {winner.pages || 0} pág.
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-2xl font-black text-slate-900">
                      {raffle.winner_order_codes.join(", ")}
                    </div>
                  )}
                </div>

                <div className="mt-2 text-xs font-semibold text-amber-800">
                  Resultado guardado permanentemente.
                </div>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-black text-slate-900">
                    Participantes
                  </div>
                  <div className="text-xs font-semibold text-slate-500">
                    {(entriesByRaffle[raffle.id] || []).length} participación(es) registradas
                  </div>
                </div>
              </div>

              <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                {(entriesByRaffle[raffle.id] || []).map((entry) => {
                  const isWinner = (raffle.winner_order_codes || []).includes(entry.order_code);
                  return (
                    <div
                      key={entry.id}
                      className={`rounded-xl p-3 text-sm ${
                        isWinner ? "border-2 border-amber-300 bg-amber-50" : "bg-white"
                      }`}
                    >
                      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                        <div className="font-black text-slate-900">
                          {isWinner ? "🏆 " : ""}{entry.order_code || "Pedido sin código"}
                        </div>
                        <div className="text-xs font-bold text-slate-500">
                          {entry.pages || 0} pág.
                        </div>
                      </div>
                      <div className={`mt-1 text-xs font-semibold ${isWinner ? "text-amber-900" : "text-slate-600"}`}>
                        {entry.client_name || "Cliente"} · {entry.client_email || ""}
                      </div>
                    </div>
                  );
                })}

                {(entriesByRaffle[raffle.id] || []).length === 0 && (
                  <div className="rounded-xl bg-white p-3 text-sm font-semibold text-slate-500">
                    Todavía no hay participaciones registradas.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 md:flex-row md:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => importFinishedOrders(raffle)}
                disabled={saving || !raffle.active}
              >
                Importar pedidos terminados
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => notifyRaffle(raffle)}
                disabled={saving || !raffle.active}
              >
                Notificar clientes
              </Button>
              {raffle.ends_at && new Date().toISOString().slice(0, 10) <= raffle.ends_at ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => drawRaffle(raffle, true)}
                  disabled={saving || raffle.winner_order_codes?.length > 0 || !(entriesByRaffle[raffle.id] || []).length}
                >
                  ⚠️ Forzar sorteo
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => drawRaffle(raffle)}
                  disabled={saving || raffle.winner_order_codes?.length > 0 || !(entriesByRaffle[raffle.id] || []).length}
                >
                  🎲 Sortear ganador
                </Button>
              )}
              <Button onClick={() => saveRaffle(raffle)} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
                {saving ? "Guardando…" : "Guardar sorteo"}
              </Button>
            </div>
          </section>
        ))}

        {raffles.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            No hay sorteos todavía.
          </div>
        )}
      </div>
    </div>
  );
}
